import React, { useState, useCallback, useMemo } from 'react'
import {
  Modal,
  Box,
  SpaceBetween,
  Button,
  ButtonDropdown,
  Header,
  Tabs,
  FormField,
  Input,
  Select,
  Textarea,
  Toggle,
  Alert,
  Container,
  Grid,
  ColumnLayout,
  Cards,
  Badge,
  Icon
} from '@cloudscape-design/components'
import { DashboardWidget } from '../../../types'
import { useConvex } from 'convex/react'
import { api } from '../../../../../../convex/_generated/api'
import { useAmplifyAuth } from '../../../../../providers/AmplifyAuthProvider'

interface ChartWidgetConfigProps {
  widget: DashboardWidget
  visible: boolean
  onDismiss: () => void
  onConfirm: (updatedWidget: DashboardWidget) => void
}

interface QueryConfig {
  id: string
  name: string
  asset: {
    id: string
    name: string
    hierarchy: string[]
    external_asset_id?: string
  }
  property: {
    id: string
    name: string
    unit?: string
    dataType: 'number' | 'string' | 'boolean'
    propertyType: 'measurement' | 'attribute' | 'metric'
  }
  
  // SiteWise query configuration
  siteWiseQuery: {
    queryType: 'GET_PROPERTY_VALUE' | 'GET_PROPERTY_VALUE_HISTORY' | 'GET_INTERPOLATED_PROPERTY_VALUES' | 'GET_PROPERTY_VALUE_AGGREGATES'
    quality: 'ANY' | 'GOOD' | 'BAD' | 'UNCERTAIN'
    timeOrdering: 'ASCENDING' | 'DESCENDING'
    resolution?: 'Auto' | 'Minute' | '15 Minutes' | 'Hour' | 'Day'
    aggregate?: 'Average' | 'Count' | 'Maximum' | 'Minimum' | 'StandardDeviation' | 'Sum'
    expandTimeRange?: boolean
    maxResults: number
  }
  
  color: string
  visible: boolean
  yAxis: 'left' | 'right'
}

const CHART_TYPES = [
  { label: 'Line Chart', value: 'line' },
  { label: 'Area Chart', value: 'area' },
  { label: 'Bar Chart', value: 'bar' }
]

const COLORS = [
  { label: 'Blue', value: '#1f77b4' },
  { label: 'Orange', value: '#ff7f0e' },
  { label: 'Green', value: '#2ca02c' },
  { label: 'Red', value: '#d62728' },
  { label: 'Purple', value: '#9467bd' },
  { label: 'Brown', value: '#8c564b' },
  { label: 'Pink', value: '#e377c2' },
  { label: 'Gray', value: '#7f7f7f' },
  { label: 'Olive', value: '#bcbd22' },
  { label: 'Cyan', value: '#17becf' },
  { label: 'Light Blue', value: '#aec7e8' },
  { label: 'Light Orange', value: '#ffbb78' }
]

// SiteWise Query Configuration Options
const SITEWISE_QUERY_TYPES = [
  {
    value: 'GET_PROPERTY_VALUE' as const,
    label: 'Current Value',
    description: 'Get the latest property value',
    showResolution: false,
    showAggregate: false,
    showExpandTimeRange: false
  },
  {
    value: 'GET_PROPERTY_VALUE_HISTORY' as const,
    label: 'Raw Historical Data',
    description: 'Get raw historical values as stored',
    showResolution: false,
    showAggregate: false,
    showExpandTimeRange: false
  },
  {
    value: 'GET_INTERPOLATED_PROPERTY_VALUES' as const,
    label: 'Interpolated Time Series',
    description: 'Get interpolated values at regular intervals',
    showResolution: true,
    showAggregate: false,
    showExpandTimeRange: false
  },
  {
    value: 'GET_PROPERTY_VALUE_AGGREGATES' as const,
    label: 'Aggregated Values',
    description: 'Get aggregated values over time windows',
    showResolution: true,
    showAggregate: true,
    showExpandTimeRange: true
  }
]

// AWS SiteWise aggregation resolution options
// Based on AWS SiteWise supported resolutions: 1m, 15m, 1h, 1d for aggregates
const RESOLUTION_OPTIONS = [
  { value: 'Auto' as const, label: 'Auto', description: 'Automatically select optimal resolution' },
  { value: 'Minute' as const, label: '1 Minute', description: 'High frequency aggregation (< 24 hours)' },
  { value: '15 Minutes' as const, label: '15 Minutes', description: 'Medium frequency aggregation (< 1 week)' },
  { value: 'Hour' as const, label: '1 Hour', description: 'Low frequency aggregation (< 1 month)' },
  { value: 'Day' as const, label: '1 Day', description: 'Daily aggregation (> 1 month)' }
]

const AGGREGATE_OPTIONS = [
  { value: 'Average' as const, label: 'Average', description: 'Mean value over time window' },
  { value: 'Count' as const, label: 'Count', description: 'Number of data points in time window' },
  { value: 'Maximum' as const, label: 'Maximum', description: 'Highest value in time window' },
  { value: 'Minimum' as const, label: 'Minimum', description: 'Lowest value in time window' },
  { value: 'StandardDeviation' as const, label: 'Standard Deviation', description: 'Variability measure over time window' },
  { value: 'Sum' as const, label: 'Sum', description: 'Total of all values in time window' }
]

const QUALITY_OPTIONS = [
  { value: 'ANY' as const, label: 'Any Quality', description: 'Include all data points regardless of quality' },
  { value: 'GOOD' as const, label: 'Good Only', description: 'Only include high-quality data points' },
  { value: 'BAD' as const, label: 'Bad Only', description: 'Only include poor-quality data points' },
  { value: 'UNCERTAIN' as const, label: 'Uncertain Only', description: 'Only include uncertain-quality data points' }
]

export const ChartWidgetConfigAdvanced: React.FC<ChartWidgetConfigProps> = ({
  widget,
  visible,
  onDismiss,
  onConfirm
}) => {
  const [activeTab, setActiveTab] = useState('general')
  const convex = useConvex()
  const { sessionId } = useAmplifyAuth()
  
  // Widget configuration state
  const [widgetName, setWidgetName] = useState(widget.title || 'Chart Widget')
  const [widgetDescription, setWidgetDescription] = useState(widget.description || '')
  
  // Chart configuration state  
  const visualization = widget.visualization || {}
  const [chartType, setChartType] = useState(visualization?.chartType || 'line')
  const [showLegend, setShowLegend] = useState(visualization?.legend?.show !== false)
  const [showGrid, setShowGrid] = useState((widget as any).showGrid !== false)
  const [leftYAxisLabel, setLeftYAxisLabel] = useState(visualization?.yAxes?.[0]?.label || '')
  const [leftYAxisUnit, setLeftYAxisUnit] = useState(visualization?.yAxes?.[0]?.unit || '')
  const [rightYAxisLabel, setRightYAxisLabel] = useState(visualization?.yAxes?.[1]?.label || '')
  const [rightYAxisUnit, setRightYAxisUnit] = useState(visualization?.yAxes?.[1]?.unit || '')
  
  // Query configuration state with proper defaults
  const [queries, setQueries] = useState<QueryConfig[]>(() => {
    const existingQueries = widget.queries || []
    return existingQueries.map(q => ({
      ...q,
      // Ensure siteWiseQuery has defaults if missing
      siteWiseQuery: q.siteWiseQuery || {
        queryType: 'GET_PROPERTY_VALUE_HISTORY',
        quality: 'ANY',
        timeOrdering: 'ASCENDING',
        maxResults: 1000
      }
    }))
  })

  // Asset and property loading
  const [assetHierarchy, setAssetHierarchy] = useState<any[]>([])
  const [assetProperties, setAssetProperties] = useState<Record<string, any[]>>({})
  const [isLoadingAssets, setIsLoadingAssets] = useState(false)
  const [isLoadingProperties, setIsLoadingProperties] = useState(false)

  // Load properties for an asset
  const loadPropertiesForAsset = useCallback(async (assetId: string) => {
    if (!sessionId || assetProperties[assetId]) return // Skip if already loaded
    
    try {
      setIsLoadingProperties(true)
      const properties = await convex.query(api["components/assets/index"].getPropertiesForAsset, {
        sessionId,
        assetId,
        propertyType: "measurement" // Only load measurement properties
      })
      
      setAssetProperties(prev => ({
        ...prev,
        [assetId]: properties
      }))
    } catch (error) {
      console.error('❌ Failed to load properties for asset', assetId, ':', error)
    } finally {
      setIsLoadingProperties(false)
    }
  }, [sessionId, convex, assetProperties])

  // Load asset hierarchy
  React.useEffect(() => {
    const loadAssets = async () => {
      if (!sessionId) return
      
      try {
        setIsLoadingAssets(true)
        const hierarchy = await convex.query(api["components/assets/index"].getAssetHierarchy, {
          sessionId
        })
        setAssetHierarchy(hierarchy)
      } catch (error) {
        console.error('Failed to load asset hierarchy:', error)
      } finally {
        setIsLoadingAssets(false)
      }
    }
    
    if (visible) {
      loadAssets()
    }
  }, [sessionId, convex, visible])

  // Load properties for existing asset selections when modal opens
  React.useEffect(() => {
    if (visible && sessionId && queries.length > 0) {
      queries.forEach(query => {
        if (query.asset.id && !assetProperties[query.asset.id]) {
          loadPropertiesForAsset(query.asset.id)
        }
      })
    }
  }, [visible, sessionId, queries, assetProperties, loadPropertiesForAsset])

  // Add new query with defaults
  const handleAddQuery = useCallback(() => {
    const newQuery: QueryConfig = {
      id: `query_${Date.now()}`,
      name: `Query ${queries.length + 1}`,
      asset: {
        id: '',
        name: '',
        hierarchy: []
      },
      property: {
        id: '',
        name: '',
        dataType: 'number',
        propertyType: 'measurement'
      },
      siteWiseQuery: {
        queryType: 'GET_PROPERTY_VALUE_HISTORY',
        quality: 'ANY',
        timeOrdering: 'ASCENDING',
        maxResults: 1000
      },
      color: COLORS[queries.length % COLORS.length].value,
      visible: true,
      yAxis: 'left'
    }
    setQueries([...queries, newQuery])
  }, [queries])

  // Remove query
  const handleRemoveQuery = useCallback((queryId: string) => {
    setQueries(queries.filter(q => q.id !== queryId))
  }, [queries])

  // Update query
  const handleUpdateQuery = useCallback((queryId: string, updates: Partial<QueryConfig>) => {
    setQueries(queries.map(q => q.id === queryId ? { ...q, ...updates } : q))
  }, [queries])

  // Duplicate query
  const handleDuplicateQuery = useCallback((originalQuery: QueryConfig) => {
    // Find the next available query number
    const existingNumbers = queries
      .map(q => q.name)
      .filter(name => name.match(/^Query \d+$/))
      .map(name => parseInt(name.replace('Query ', '')))
      .filter(num => !isNaN(num))
    
    const maxNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) : 0
    const newQueryNumber = maxNumber + 1
    
    const duplicatedQuery: QueryConfig = {
      ...originalQuery,
      id: `query_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: `Query ${newQueryNumber}`,
      color: COLORS[(queries.length) % COLORS.length] // Use next available color
    }
    setQueries([...queries, duplicatedQuery])
  }, [queries])

  // Save widget configuration
  const handleSave = useCallback(() => {
    const updatedWidget: DashboardWidget = {
      ...widget,
      title: widgetName,
      description: widgetDescription,
      queries,
      visualization: {
        chartType,
        legend: { show: showLegend, position: 'bottom' },
        yAxes: [
          {
            id: 'left',
            label: leftYAxisLabel,
            unit: leftYAxisUnit
          },
          {
            id: 'right',
            label: rightYAxisLabel,
            unit: rightYAxisUnit
          }
        ],
        showGrid,
        colors: COLORS.map(c => c.value),
        strokeWidth: 2,
        tooltip: { show: true },
        zoom: { enabled: true, resetOnNewData: false }
      }
    }
    
    onConfirm(updatedWidget)
  }, [widget, widgetName, widgetDescription, queries, chartType, showLegend, leftYAxisLabel, leftYAxisUnit, rightYAxisLabel, rightYAxisUnit, showGrid, onConfirm])

  // Asset options for Select - traverse the full hierarchy
  const assetOptions = useMemo(() => {
    const options: Array<{ label: string; value: string; description?: string }> = []
    
    const addAssetOptions = (assets: any[], level: number = 0, prefix: string = '') => {
      assets.forEach(asset => {
        const indent = '  '.repeat(level)
        const typePrefix = level === 0 ? '🏭 ' : level === 1 ? '🏗️ ' : '⚙️ '
        
        options.push({
          label: `${indent}${typePrefix}${asset.name}`,
          value: asset._id,
          description: `${asset.external_asset_id || asset.sitewise_asset_id || 'Internal ID'}`
        })
        
        // Handle production lines (nested under factories)
        if (asset.productionLines && asset.productionLines.length > 0) {
          addAssetOptions(asset.productionLines, level + 1, asset.name)
        }
        
        // Handle equipment (nested under production lines)
        if (asset.equipment && asset.equipment.length > 0) {
          addAssetOptions(asset.equipment, level + 1, `${prefix} > ${asset.name}`)
        }
      })
    }
    
    addAssetOptions(assetHierarchy)
    return options
  }, [assetHierarchy])

  // Property options for selected asset
  const getPropertyOptions = useCallback((assetId: string) => {
    const properties = assetProperties[assetId] || []
    return properties.map(prop => ({
      label: `${prop.name} (${prop.unit || 'no unit'})`,
      value: prop.id,
      description: `${prop.dataType} • ${prop.propertyType}`
    }))
  }, [assetProperties])

  if (!visible) return null

  return (
    <Modal
      size="large"
      onDismiss={onDismiss}
      visible={visible}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save Configuration
            </Button>
          </SpaceBetween>
        </Box>
      }
      header="Configure Chart Widget"
    >
      <Tabs
        activeTabId={activeTab}
        onChange={({ detail }) => setActiveTab(detail.activeTabId)}
        tabs={[
          {
            label: 'General',
            id: 'general',
            content: (
              <SpaceBetween size="m">
                <Container header={<Header variant="h3">Widget Settings</Header>}>
                  <SpaceBetween size="m">
                    <FormField label="Widget Title">
                      <Input
                        value={widgetName}
                        onChange={({ detail }) => setWidgetName(detail.value)}
                        placeholder="Enter widget title"
                      />
                    </FormField>
                    
                    <FormField label="Description" description="Optional description for this widget">
                      <Textarea
                        value={widgetDescription}
                        onChange={({ detail }) => setWidgetDescription(detail.value)}
                        placeholder="Enter widget description"
                        rows={2}
                      />
                    </FormField>
                  </SpaceBetween>
                </Container>

                <Container header={<Header variant="h3">Chart Settings</Header>}>
                  <ColumnLayout columns={2}>
                    <FormField label="Chart Type">
                      <Select
                        selectedOption={CHART_TYPES.find(t => t.value === chartType) || null}
                        onChange={({ detail }) => setChartType(detail.selectedOption?.value || 'line')}
                        options={CHART_TYPES}
                      />
                    </FormField>
                    
                    <FormField label="Left Y-Axis Label">
                      <Input
                        value={leftYAxisLabel}
                        onChange={({ detail }) => setLeftYAxisLabel(detail.value)}
                        placeholder="Primary axis label"
                      />
                    </FormField>
                    
                    <FormField label="Left Y-Axis Unit">
                      <Input
                        value={leftYAxisUnit}
                        onChange={({ detail }) => setLeftYAxisUnit(detail.value)}
                        placeholder="Unit (e.g., °C, %)"
                      />
                    </FormField>
                    
                    <FormField label="Right Y-Axis Label">
                      <Input
                        value={rightYAxisLabel}
                        onChange={({ detail }) => setRightYAxisLabel(detail.value)}
                        placeholder="Secondary axis label"
                      />
                    </FormField>
                    
                    <FormField label="Right Y-Axis Unit">
                      <Input
                        value={rightYAxisUnit}
                        onChange={({ detail }) => setRightYAxisUnit(detail.value)}
                        placeholder="Unit (e.g., PSI, RPM)"
                      />
                    </FormField>
                    
                    <div>
                      <FormField label="Display Options">
                        <SpaceBetween size="s">
                          <Toggle
                            checked={showLegend}
                            onChange={({ detail }) => setShowLegend(detail.checked)}
                          >
                            Show Legend
                          </Toggle>
                          <Toggle
                            checked={showGrid}
                            onChange={({ detail }) => setShowGrid(detail.checked)}
                          >
                            Show Grid
                          </Toggle>
                        </SpaceBetween>
                      </FormField>
                    </div>
                  </ColumnLayout>
                </Container>
              </SpaceBetween>
            )
          },
          {
            label: 'Data Sources',
            id: 'data',
            content: (
              <SpaceBetween size="m">
                <Container 
                  header={
                    <Header
                      variant="h3"
                      actions={
                        <Button
                          iconName="add-plus"
                          onClick={handleAddQuery}
                        >
                          Add Data Source
                        </Button>
                      }
                    >
                      Configure Data Sources ({queries.length})
                    </Header>
                  }
                >
                  {queries.length === 0 ? (
                    <Box textAlign="center" color="text-body-secondary" padding="l">
                      <SpaceBetween size="m">
                        <Icon name="analytics" size="big" />
                        <div>No data sources configured</div>
                        <div style={{ fontSize: '14px' }}>
                          Add data sources to display real-time IoT data from AWS SiteWise
                        </div>
                        <Button iconName="add-plus" onClick={handleAddQuery}>
                          Add Your First Data Source
                        </Button>
                      </SpaceBetween>
                    </Box>
                  ) : (
                    <Cards
                      cardDefinition={{
                        header: (item: QueryConfig) => (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Header variant="h4">
                              {item.name}
                              <Badge color={item.visible ? 'green' : 'grey'}>
                                {item.visible ? 'Visible' : 'Hidden'}
                              </Badge>
                            </Header>
                            <ButtonDropdown
                              items={[
                                { id: 'duplicate', text: 'Duplicate', iconName: 'copy' },
                                { id: 'toggle', text: item.visible ? 'Hide Series' : 'Show Series', iconName: item.visible ? 'view-off' : 'view-full' },
                                { id: 'divider', itemType: 'divider' },
                                { id: 'remove', text: 'Remove Series', iconName: 'remove' }
                              ]}
                              onItemClick={({ detail }) => {
                                if (detail.id === 'remove') {
                                  handleRemoveQuery(item.id)
                                } else if (detail.id === 'toggle') {
                                  handleUpdateQuery(item.id, { visible: !item.visible })
                                } else if (detail.id === 'duplicate') {
                                  handleDuplicateQuery(item)
                                }
                              }}
                              variant="icon"
                              ariaLabel="Query actions"
                            />
                          </div>
                        ),
                        sections: [
                          {
                            id: 'asset-selection',
                            header: 'Asset & Property',
                            content: (item: QueryConfig) => (
                              <ColumnLayout columns={2}>
                                <FormField label="Asset">
                                  <Select
                                    selectedOption={assetOptions.find(opt => opt.value === item.asset.id) || null}
                                    onChange={({ detail }) => {
                                      const selectedAssetId = detail.selectedOption?.value
                                      if (!selectedAssetId) return
                                      
                                      // Find the selected asset in the hierarchy (could be factory, line, or equipment)
                                      let selectedAsset = null
                                      let assetHierarchyPath: string[] = []
                                      
                                      // Search through the nested hierarchy
                                      for (const factory of assetHierarchy) {
                                        if (factory._id === selectedAssetId) {
                                          selectedAsset = factory
                                          assetHierarchyPath = [factory.name]
                                          break
                                        }
                                        
                                        // Search production lines
                                        if (factory.productionLines) {
                                          for (const line of factory.productionLines) {
                                            if (line._id === selectedAssetId) {
                                              selectedAsset = line
                                              assetHierarchyPath = [factory.name, line.name]
                                              break
                                            }
                                            
                                            // Search equipment
                                            if (line.equipment) {
                                              for (const equipment of line.equipment) {
                                                if (equipment._id === selectedAssetId) {
                                                  selectedAsset = equipment
                                                  assetHierarchyPath = [factory.name, line.name, equipment.name]
                                                  break
                                                }
                                              }
                                            }
                                            if (selectedAsset) break
                                          }
                                        }
                                        if (selectedAsset) break
                                      }
                                      
                                      if (selectedAsset) {
                                        handleUpdateQuery(item.id, {
                                          asset: {
                                            id: selectedAsset._id,
                                            name: selectedAsset.name,
                                            hierarchy: assetHierarchyPath,
                                            external_asset_id: selectedAsset.external_asset_id
                                          }
                                        })
                                        loadPropertiesForAsset(selectedAsset._id)
                                      }
                                    }}
                                    options={assetOptions}
                                    loadingText="Loading assets..."
                                    statusType={isLoadingAssets ? 'loading' : 'finished'}
                                    placeholder="Select an asset"
                                  />
                                </FormField>
                                
                                <FormField label="Property">
                                  <Select
                                    selectedOption={getPropertyOptions(item.asset.id).find(opt => opt.value === item.property.id) || null}
                                    onChange={({ detail }) => {
                                      const properties = assetProperties[item.asset.id] || []
                                      const selectedProperty = properties.find(p => p.id === detail.selectedOption?.value)
                                      if (selectedProperty) {
                                        handleUpdateQuery(item.id, {
                                          property: {
                                            id: selectedProperty.id,
                                            external_property_id: selectedProperty.external_property_id,
                                            name: selectedProperty.name,
                                            unit: selectedProperty.unit,
                                            dataType: selectedProperty.dataType || 'number',
                                            propertyType: selectedProperty.propertyType || 'measurement'
                                          }
                                        })
                                      }
                                    }}
                                    options={getPropertyOptions(item.asset.id)}
                                    disabled={!item.asset.id}
                                    loadingText="Loading properties..."
                                    statusType={isLoadingProperties ? 'loading' : 'finished'}
                                    placeholder={item.asset.id ? "Select a property" : "Select an asset first"}
                                  />
                                </FormField>
                              </ColumnLayout>
                            )
                          },
                          {
                            id: 'query-config',
                            header: 'SiteWise Query Configuration',
                            content: (item: QueryConfig) => {
                              const queryTypeConfig = SITEWISE_QUERY_TYPES.find(qt => qt.value === item.siteWiseQuery?.queryType)
                              
                              return (
                                <SpaceBetween size="m">
                                  <ColumnLayout columns={2}>
                                    <FormField 
                                      label="Query Type"
                                      description="Determines how data is retrieved from AWS SiteWise"
                                    >
                                      <Select
                                        selectedOption={SITEWISE_QUERY_TYPES.find(qt => qt.value === item.siteWiseQuery?.queryType) || null}
                                        onChange={({ detail }) => {
                                          const queryType = detail.selectedOption?.value as any
                                          handleUpdateQuery(item.id, {
                                            siteWiseQuery: {
                                              ...(item.siteWiseQuery || {}),
                                              queryType,
                                              // Reset dependent fields when changing query type
                                              ...(queryType !== 'GET_INTERPOLATED_PROPERTY_VALUES' && queryType !== 'GET_PROPERTY_VALUE_AGGREGATES' && { resolution: undefined }),
                                              ...(queryType !== 'GET_PROPERTY_VALUE_AGGREGATES' && { aggregate: undefined })
                                            }
                                          })
                                        }}
                                        options={SITEWISE_QUERY_TYPES.map(qt => ({
                                          label: qt.label,
                                          value: qt.value,
                                          description: qt.description
                                        }))}
                                      />
                                    </FormField>
                                    
                                    <FormField label="Data Quality">
                                      <Select
                                        selectedOption={QUALITY_OPTIONS.find(qo => qo.value === item.siteWiseQuery?.quality) || null}
                                        onChange={({ detail }) => {
                                          handleUpdateQuery(item.id, {
                                            siteWiseQuery: {
                                              ...(item.siteWiseQuery || {}),
                                              quality: detail.selectedOption?.value as any
                                            }
                                          })
                                        }}
                                        options={QUALITY_OPTIONS.map(qo => ({
                                          label: qo.label,
                                          value: qo.value,
                                          description: qo.description
                                        }))}
                                      />
                                    </FormField>

                                    {queryTypeConfig?.showResolution && (
                                      <FormField label="Resolution">
                                        <Select
                                          selectedOption={RESOLUTION_OPTIONS.find(ro => ro.value === item.siteWiseQuery?.resolution) || null}
                                          onChange={({ detail }) => {
                                            handleUpdateQuery(item.id, {
                                              siteWiseQuery: {
                                                ...(item.siteWiseQuery || {}),
                                                resolution: detail.selectedOption?.value as any
                                              }
                                            })
                                          }}
                                          options={RESOLUTION_OPTIONS.map(ro => ({
                                            label: ro.label,
                                            value: ro.value,
                                            description: ro.description
                                          }))}
                                        />
                                      </FormField>
                                    )}

                                    {queryTypeConfig?.showAggregate && (
                                      <FormField label="Aggregation Function">
                                        <Select
                                          selectedOption={AGGREGATE_OPTIONS.find(ao => ao.value === item.siteWiseQuery?.aggregate) || null}
                                          onChange={({ detail }) => {
                                            handleUpdateQuery(item.id, {
                                              siteWiseQuery: {
                                                ...(item.siteWiseQuery || {}),
                                                aggregate: detail.selectedOption?.value as any
                                              }
                                            })
                                          }}
                                          options={AGGREGATE_OPTIONS.map(ao => ({
                                            label: ao.label,
                                            value: ao.value,
                                            description: ao.description
                                          }))}
                                        />
                                      </FormField>
                                    )}
                                  </ColumnLayout>

                                  {queryTypeConfig && (
                                    <Alert type="info" statusIconAriaLabel="Info">
                                      <strong>{queryTypeConfig.label}:</strong> {queryTypeConfig.description}
                                    </Alert>
                                  )}
                                </SpaceBetween>
                              )
                            }
                          },
                          {
                            id: 'styling-config',
                            header: 'Series Styling',
                            content: (item: QueryConfig) => (
                              <ColumnLayout columns={2}>
                                <FormField 
                                  label="Y-Axis Assignment"
                                  description="Choose which Y-axis this series should use"
                                >
                                  <Select
                                    selectedOption={{
                                      label: item.yAxis === 'left' ? 'Left Y-Axis' : 'Right Y-Axis',
                                      value: item.yAxis
                                    }}
                                    onChange={({ detail }) => {
                                      handleUpdateQuery(item.id, {
                                        yAxis: detail.selectedOption?.value as 'left' | 'right'
                                      })
                                    }}
                                    options={[
                                      { label: 'Left Y-Axis', value: 'left', description: 'Primary axis (left side)' },
                                      { label: 'Right Y-Axis', value: 'right', description: 'Secondary axis (right side)' }
                                    ]}
                                  />
                                </FormField>
                                
                                <FormField label="Series Color">
                                  <Select
                                    selectedOption={COLORS.find(c => c.value === item.color) ? {
                                      label: COLORS.find(c => c.value === item.color)?.label || '',
                                      value: item.color
                                    } : null}
                                    onChange={({ detail }) => {
                                      handleUpdateQuery(item.id, {
                                        color: detail.selectedOption?.value || COLORS[0].value
                                      })
                                    }}
                                    options={COLORS.map(color => ({
                                      label: color.label,
                                      value: color.value
                                    }))}
                                  />
                                </FormField>
                              </ColumnLayout>
                            )
                          }
                        ]
                      }}
                      cardsPerRow={[{ cards: 1 }]}
                      items={queries}
                    />
                  )}
                </Container>
              </SpaceBetween>
            )
          }
        ]}
      />
    </Modal>
  )
}